import React, { useState, useEffect, useRef, useMemo, useCallback, useSyncExternalStore } from 'react';
import {
  Alert,
  Snackbar,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Popover,
  Collapse,
  Badge,
  useTheme,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
  DialogContentText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Today as TodayIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Cancel as CancelIcon,
  Note as NoteIcon,
  FilterList as FilterIcon,
  ScheduleSend as ScheduleSendIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenTaskIcon,
  DateRange as SpreadIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  startOfWeek, 
  endOfWeek, 
  startOfDay,
  endOfDay,
  addDays, 
  subDays,
  addWeeks,
  subWeeks,
  isSameDay,
  isWithinInterval,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  format
} from 'date-fns';
import { Appointment, AppointmentStatus, Specialist } from '../types';
import { editorDuration } from '../components/Calendar/appointmentClock';
import AppointmentModal from '../components/Calendar/AppointmentModal';
import QuickScheduleModal from '../components/Calendar/QuickScheduleModal';
import AppointmentRowActions from '../components/Calendar/AppointmentRowActions';
import { useNavigate, useLocation } from 'react-router-dom';
import { appointmentsApi, specialistsApi, schedulingApi, triageApi, schedulingLinksApi, settingsApi } from '../services/api';
import { suggestSpread, addMonths as addYearMonth } from './recallSpread';
import { cancellationNotice } from '../components/Dashboard/cancellationNotice';
import { nonWorkingWeekdays, WEEKDAY_CLASS } from './calendarWorkingDays';
import { toPracticeWallClock, practiceTimeLabel, practiceDateTimeLabel, viewerIsElsewhere, getPracticeTimeZone, subscribePracticeTimeZone } from '../utils/practiceTime';
import { summarizeProviderSelection, matchesProviderFilter } from './calendarProviderFilter';
import { canCancelAppointment } from '../components/Calendar/appointmentActions';
import { useAuthStore } from '../store/authStore';
import { formatDateOnly, formatTimeOnly, formatClockString } from '../utils/dateTime';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { readCalendarLocation, buildCalendarReturnTo, CalendarView } from './calendarLocation';
import { resolveWellnessContact, ContactCandidate } from './wellnessContactLink';
import { compactRangeLabel } from './calendarRangeLabel';
import { describeRecallRow } from './wellnessRowState';
import { undoableTaskFrom } from './wellnessUndo';
import { gridTimeWindow } from './calendarTimeWindow';
import { STATUS_FILTER_OPTIONS, WORKFLOW_STATUS_OPTIONS } from './appointmentStatuses';
import MobileRecordCard from '../components/common/MobileRecordCard';

type ViewMode = 'list' | 'day' | 'week' | 'month';

// Helper to parse datetime as local time (remove Z suffix to prevent UTC conversion)
// Appointment times come back as UTC instants ("2026-08-28T15:30:00.000Z").
// This used to strip the Z and re-read the instant as if it were local, which
// shifted every appointment by the UTC offset: a 10:30 AM booking rendered as
// 3:30 AM, and anything after ~7 PM local moved to the NEXT day and vanished
// from its own day's view. Parsing the string as-is converts UTC -> local
// correctly, and a naive string with no zone still parses as local.
// Every date calculation on this page — which week an appointment falls in,
// which day, how the list sorts — goes through here. It used to be `new Date()`
// on the raw instant, which answers in the viewer's timezone: an appointment
// late in the clinic day could be counted on the following day for a viewer
// east of Chicago. It now resolves to the clinic's wall-clock first.
const parseLocalTime = (dateTimeStr: string | Date): Date => {
  if (dateTimeStr instanceof Date) return dateTimeStr;
  const wallClock = toPracticeWallClock(dateTimeStr);
  return new Date(wallClock || dateTimeStr);
};

// Provider colours. Chosen deliberately, not generated: hue-spread so they stay
// separable for the common colour-vision deficiencies, and dark enough that the
// white label text on each event keeps a 4.5:1 contrast ratio (the previous
// 8-colour set failed that on three of its colours).
//
// Worst-case separation under deuteranopia/protanopia, CIE deltaE (>=25 is
// comfortably distinct):  2 providers 128.5 · 3 providers 44.4 · 4 providers 17.7
//
// Capped at four on purpose. Under red-green deficiency the visible spectrum
// collapses toward a blue-yellow axis, so genuinely distinct hues run out around
// four or five — a longer list would only look distinct to normal vision. Beyond
// four providers colours repeat, and the provider NAME on the event is what
// disambiguates. Colour here is reinforcement, never the only cue.
const PROVIDER_PALETTE = [
  '#1d4ed8', // blue
  '#b45309', // amber
  '#0f766e', // teal
  '#9d174d', // magenta
];

const hashProviderId = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
};

const buildProviderColorMap = (list: Specialist[]) => {
  const used = new Set<string>();
  const map: Record<string, string> = {};
  // Assign in a stable id order rather than whatever order the API returned.
  // Previously the mapping depended on list order, so a provider's colour could
  // silently change when the list was re-fetched or reordered. Adding a new
  // provider can still shift colours if it sorts ahead of an existing one and
  // takes a slot — accepted, because guaranteeing two providers never share a
  // colour matters more day to day than a permanently fixed hue.
  [...list]
    .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    .forEach((p) => {
    let idx = hashProviderId(String(p.id)) % PROVIDER_PALETTE.length;
    let tries = 0;
    while (used.has(PROVIDER_PALETTE[idx]) && tries < PROVIDER_PALETTE.length) {
      idx = (idx + 1) % PROVIDER_PALETTE.length;
      tries += 1;
    }
    map[p.id] = PROVIDER_PALETTE[idx];
    used.add(PROVIDER_PALETTE[idx]);
    });
  return map;
};

const STATUS_OPTIONS = STATUS_FILTER_OPTIONS;

function patientTextSuffix(patientText: { sent?: boolean; reason?: string } | null | undefined): string {
  if (!patientText) return '';
  if (patientText.sent) return 'The patient was texted.';
  if (patientText.reason === 'no-conversation') return 'There was no text thread, so the patient was not texted.';
  return 'The text to the patient could not be sent.';
}

const Calendar: React.FC = () => {
  const practiceZone = useSyncExternalStore(subscribePracticeTimeZone, getPracticeTimeZone, getPracticeTimeZone);
  // App chrome + table->card switches key off `down('md')` (see plan).
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Opens on the current week — the view Allison works out of all day.
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  // Mobile: Status + Providers collapse behind a "Filters" toggle so the
  // appointment list owns the screen; date-nav stays always visible.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [providers, setProviders] = useState<Specialist[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  // A provider asked for in the URL. Held in a ref because the provider list
  // loads afterwards and would otherwise select everyone over the top of it.
  const navigate = useNavigate();
  const location = useLocation();

  // Arriving from a task's scheduling panel, so there is somewhere to go back
  // to. Same shape the Contacts and patient-chart flows already use: the
  // sender puts { returnTo, backLabel } in navigation state, and the dashboard
  // reopens the task from ?taskId=. Held in local state because the state is
  // cleared immediately — otherwise a later navigation replays it.
  const [taskReturn, setTaskReturn] = useState<{ to: string; label: string } | null>(null);
  const taskReturnHandledRef = useRef(false);
  useEffect(() => {
    const st = (location.state as any) || {};
    const q = new URLSearchParams(location.search);
    const returnTo = st.returnTo || q.get('returnTo');
    if (!returnTo || taskReturnHandledRef.current) return;
    taskReturnHandledRef.current = true;
    setTaskReturn({ to: returnTo, label: st.backLabel || q.get('backLabel') || 'Back' });
  }, [location.state, location.search]);
  const requestedProvider = useRef<string | null>(null);
  // Who is due an annual wellness exam in the month on screen. Patients
  // currently start that conversation themselves; this is the office seeing it
  // first. Loaded only while the month view is open.
  const [wellnessDue, setWellnessDue] = useState<Array<{
    patientId: string; patientName: string; patientPhone?: string;
    lastWellness: string; monthsSince: number; overdue: boolean;
    dueThisMonth: boolean; status: string; statusUpdatedAt?: string | null;
    taskId?: string | null; addedByHand?: boolean;
    contactCandidates?: ContactCandidate[];
    // Where the last-seen date came from, and whether the office planned this
    // patient for the month on screen.
    encounterSource?: string | null; dueMonth?: string | null; plannedThisMonth?: boolean;
  }>>([]);
  // Moving one patient to a month of the office's choosing.
  const [wellnessMoveFor, setWellnessMoveFor] = useState<string | null>(null);
  const [wellnessMoveMonth, setWellnessMoveMonth] = useState('');
  // A suggested spread of the overdue backlog. Proposed here, applied only on
  // a click; the cap starts from Admin > Settings and can be changed per run.
  const [spreadOpen, setSpreadOpen] = useState(false);
  const [spreadAsideOpen, setSpreadAsideOpen] = useState(false);
  const [spreadMonths, setSpreadMonths] = useState(3);
  const [spreadCap, setSpreadCap] = useState(20);
  const [spreadIncludeImported, setSpreadIncludeImported] = useState(true);
  const [spreadEdits, setSpreadEdits] = useState<Record<string, string>>({});
  const [spreadBusy, setSpreadBusy] = useState(false);
  // What earlier runs already planned into the coming months, per YYYY-MM.
  const [spreadPlanned, setSpreadPlanned] = useState<Record<string, number>>({});
  const [wellnessLoading, setWellnessLoading] = useState(false);
  const [wellnessBusy, setWellnessBusy] = useState<string | null>(null);
  const [wellnessMenu, setWellnessMenu] = useState<{ el: HTMLElement; patientId: string } | null>(null);
  // Adding somebody the encounter history missed.
  const [wellnessAddOpen, setWellnessAddOpen] = useState(false);
  const [wellnessSearch, setWellnessSearch] = useState('');
  const [wellnessResults, setWellnessResults] = useState<Array<{
    patientId: string; patientName: string; patientPhone?: string;
  }>>([]);
  const [wellnessSearching, setWellnessSearching] = useState(false);

  // A scheduling task can hand off to the calendar with ?date= and ?provider=,
  // so "Open in calendar" lands on the day and provider being discussed rather
  // than on today. Read once on mount; afterwards the calendar is its own.
  useEffect(() => {
    const asked = readCalendarLocation(window.location.search);
    if (asked.date) setCurrentDate(new Date(`${asked.date}T00:00:00`));
    if (asked.view) setViewMode(asked.view as ViewMode);
    if (asked.provider) {
      requestedProvider.current = asked.provider;
      setSelectedProviders([asked.provider]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const providerColorMap = useMemo(() => buildProviderColorMap(providers), [providers]);

  const colorForAppointment = (apt: Appointment) => {
    const match = providers.find((p) =>
      p.name.toLowerCase() === (apt.provider || '').toLowerCase() ||
      p.id === apt.provider ||
      p.id === (apt as any).specialistId
    );
    if (match && providerColorMap[match.id]) return providerColorMap[match.id];
    return '#6b7280';
  };
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuickScheduleOpen, setIsQuickScheduleOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  // Set only while duplicating: the modal seeds its fields from this while
  // still creating rather than updating.
  const [duplicateSource, setDuplicateSource] = useState<Appointment | null>(null);
  // The cancel text goes out on its own. This checkbox only opens a follow-up
  // draft on the task; a person still presses Send for that one. Reset every
  // time the dialog opens so a choice made once never carries into the next.
  const notifyPatientRef = useRef(false);
  const [notifyPatient, setNotifyPatient] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const calendarRef = useRef<any>(null);
  const dateTimeFormat = useAuthStore((state) => state.user?.dateTimeFormat);

  // Persist a status change from the calendar's status dropdown. Previously
  // this was a console.log TODO, so the value changed on screen and silently
  // never saved. Optimistic update with rollback so the cell can't lie about
  // what's stored.
  const handleStatusChange = async (apt: Appointment, nextStatus: AppointmentStatus) => {
    const previous = apt.status;
    setAppointments((prev) => prev.map((a) => (a.id === apt.id ? { ...a, status: nextStatus } : a)));
    try {
      await schedulingApi.updateAppointment(apt.id, { status: nextStatus });
      // Reconcile with the server so the cell can't drift from stored truth.
      await fetchAppointments();
      showSnackbar(`${apt.patientName} — status set to ${formatStatusLabel(nextStatus)}`);
    } catch (error) {
      console.error('Failed to update appointment status:', error);
      setAppointments((prev) => prev.map((a) => (a.id === apt.id ? { ...a, status: previous } : a)));
      showSnackbar('Could not update the status. It has been put back — please try again.', 'error');
    }
  };

  // Confirmation feedback. Staff who don't see a booking confirmed assume it
  // failed and rebook — that's the double-booking path — so every mutation now
  // reports success explicitly, not just failure.
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
    // Starting outreach on the wrong row is noticed here, seconds later, by
    // whoever clicked. The way back belongs on this message rather than six
    // steps away in the task modal.
    undoTaskId?: string | null;
  }>({
    open: false, message: '', severity: 'success',
  });
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Confirmations name the patient and the slot rather than saying "Saved", so
  // staff can confirm it booked what they intended without hunting the grid.
  const describeAppointment = useCallback((apt: Appointment) => {
    try {
      const d = parseLocalTime(apt.startTime);
      return `for ${apt.patientName} on ${formatDateOnly(d, dateTimeFormat)} at ${formatTimeOnly(d, dateTimeFormat, false)}`;
    } catch {
      return `for ${apt.patientName || 'patient'}`;
    }
  }, [dateTimeFormat]);

  // The date window currently on screen. Mirrors the filter below exactly —
  // if these ever diverge, appointments get fetched but filtered out (or worse,
  // needed ones are never fetched).
  const visibleRange = useMemo(() => {
    if (viewMode === 'month') {
      return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    }
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 0 }),
        end: endOfWeek(currentDate, { weekStartsOn: 0 }),
      };
    }
    return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
  }, [currentDate, viewMode]);

  // Scoped to the visible range instead of pulling everything (the endpoint
  // filters server-side and otherwise returns up to 1000 rows per refresh).
  // Two fetches are in flight whenever the calendar is opened at a date rather
  // than at today: one for today, fired on mount, and one for the date the URL
  // asked for, fired when that date lands in state. Whichever answers last used
  // to win, so arriving from "Back to calendar" could paint an empty week over
  // a full one — the data was fetched, then thrown away.
  //
  // Each request takes a number and only the newest may paint.
  const appointmentsRequestRef = useRef(0);

  // Times held for patients' booking links, drawn alongside appointments so
  // the office can see why a slot is not on offer. Fetched with the
  // appointments and painted under the same request number.
  const [holds, setHolds] = useState<any[]>([]);

  const fetchAppointments = useCallback(async () => {
    const requestId = ++appointmentsRequestRef.current;
    try {
      const [appointmentsRes, holdsRes] = await Promise.all([
        appointmentsApi.getAppointments({
          startDate: visibleRange.start.toISOString(),
          endDate: visibleRange.end.toISOString(),
        }),
        schedulingLinksApi.holds(format(visibleRange.start, 'yyyy-MM-dd'), format(visibleRange.end, 'yyyy-MM-dd'))
          .catch(() => ({ data: { data: [] } })),
      ]);
      if (requestId !== appointmentsRequestRef.current) return;
      const apptData = appointmentsRes.data?.appointments || appointmentsRes.data || [];
      setAppointments(Array.isArray(apptData) ? apptData : []);
      const holdData = holdsRes.data?.data || [];
      setHolds(Array.isArray(holdData) ? holdData : []);
    } catch (error) {
      if (requestId !== appointmentsRequestRef.current) return;
      console.error("Failed to fetch appointments:", error);
    }
  }, [visibleRange]);

  // Providers change rarely — fetch once.
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const providersRes = await specialistsApi.getActive();
        // API returns array directly, not wrapped in { specialists: [...] }
        const providerData = providersRes.data?.specialists || providersRes.data || [];
        const providersArray = Array.isArray(providerData) ? providerData : [];
        setProviders(providersArray);
        const asked = requestedProvider.current;
        const isReal = asked && providersArray.some((p: Specialist) => p.id === asked);
        // Honour a provider handed over from a task; otherwise show everyone.
        setSelectedProviders(isReal ? [asked as string] : providersArray.map((p: Specialist) => p.id));
      } catch (error) {
        console.error('Failed to fetch providers:', error);
      }
    };
    fetchProviders();
  }, []);

  // Appointments follow the visible window: this runs on mount AND whenever the
  // user changes date or view. Previously the initial fetch had [] deps, so
  // navigating dates never refetched — it only worked because everything was
  // pulled up front.
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await fetchAppointments();
      setIsLoading(false);
    };
    load();
  }, [fetchAppointments]);

  useEffect(() => {
    if (viewMode !== 'month') return;
    let cancelled = false;
    const load = async () => {
      setWellnessLoading(true);
      try {
        const res = await schedulingApi.getWellnessDue(format(currentDate, 'yyyy-MM'));
        const rows = res.data?.data || res.data || [];
        if (!cancelled) setWellnessDue(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error('Failed to load wellness recall list:', err);
        if (!cancelled) setWellnessDue([]);
      } finally {
        if (!cancelled) setWellnessLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [viewMode, currentDate]);

  const reloadWellness = useCallback(async () => {
    try {
      const res = await schedulingApi.getWellnessDue(format(currentDate, 'yyyy-MM'));
      setWellnessDue(res.data?.data || []);
    } catch (err) {
      console.error('Failed to reload wellness recall list:', err);
    }
  }, [currentDate]);

  // Start the scheduling conversation: creates the task the office works from
  // and records the contact, so the list stops showing them as untouched.
  const handleStartWellnessScheduling = async (patientId: string) => {
    setWellnessBusy(patientId);
    try {
      const res = await schedulingApi.startWellnessScheduling(patientId);
      await reloadWellness();
      setSnackbar({
        open: true,
        severity: 'success',
        message: res.data?.existing
          ? 'That patient already has a scheduling task open.'
          : 'Scheduling task created — on the dashboard under Appointment. Nothing was sent.',
        // Only a task this click created: the endpoint hands back an
        // already-open one rather than starting a second, and undoing that
        // would delete work somebody else began.
        undoTaskId: undoableTaskFrom(res.data),
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || 'Could not start scheduling for that patient.',
      });
    } finally {
      setWellnessBusy(null);
    }
  };

  const handleUndoWellnessRecord = async (patientId: string) => {
    setWellnessMenu(null);
    setWellnessBusy(patientId);
    try {
      await schedulingApi.clearWellnessRecord(patientId);
      await reloadWellness();
      setSnackbar({ open: true, severity: 'success', message: 'Record cleared.' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || 'Could not clear that record.',
      });
      // A 409 means a task is in the way; the message from the API names it.

    } finally {
      setWellnessBusy(null);
    }
  };

  // Plan the month one patient is contacted in. Moving is not contacting:
  // their status is untouched, and past that month they come back as overdue.
  const handleMoveWellnessMonth = async (patientId: string, month: string | null) => {
    setWellnessMenu(null);
    setWellnessMoveFor(null);
    setWellnessBusy(patientId);
    try {
      await schedulingApi.setWellnessMonth(patientId, month);
      await reloadWellness();
      setSnackbar({
        open: true, severity: 'success',
        message: month ? `Planned for ${format(new Date(`${month}-15T12:00:00`), 'MMMM yyyy')}. Nothing was sent.` : 'Planned month cleared.',
      });
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.error || 'Could not plan that month.' });
    } finally {
      setWellnessBusy(null);
    }
  };

  // The backlog a spread is for: at least 18 months overdue, nothing done yet.
  // Every row on dev is anchored on an imported visit date; the checkbox is
  // the office's way to leave those out once hand-entered visits exist.
  const spreadCandidates = useMemo(
    () => wellnessDue.filter((p) =>
      !p.addedByHand && Number(p.monthsSince) >= 18 && p.status === 'not_started' && !p.taskId
      && (spreadIncludeImported || p.encounterSource !== 'pf_import')),
    [wellnessDue, spreadIncludeImported],
  );
  // What the rule needs per row: who can be texted, who is already planned
  // for this month by an earlier run. The rule decides; see recallSpread.ts.
  const spreadInput = useMemo(
    () => spreadCandidates.map((p) => ({
      patientId: p.patientId, monthsSince: p.monthsSince,
      patientPhone: p.patientPhone || '', patientName: p.patientName || '',
      plannedForThisMonth: Boolean(p.plannedThisMonth),
    })),
    [spreadCandidates],
  );
  const spreadImportedCount = useMemo(
    () => wellnessDue.filter((p) => !p.addedByHand && Number(p.monthsSince) >= 18 && p.status === 'not_started' && !p.taskId && p.encounterSource === 'pf_import').length,
    [wellnessDue],
  );
  const spreadFrom = format(currentDate, 'yyyy-MM');
  const spreadProposal = useMemo(
    () => suggestSpread({ patients: spreadInput, fromMonth: spreadFrom, months: spreadMonths, capPerMonth: spreadCap, alreadyPlanned: spreadPlanned }),
    [spreadInput, spreadFrom, spreadMonths, spreadCap, spreadPlanned],
  );
  // Rows the rule set aside: not offered a month select, listed with why.
  const spreadSetAside = useMemo(() => {
    const ids = new Set([...spreadProposal.kept, ...spreadProposal.leftOut.noPhone, ...spreadProposal.leftOut.noName]);
    return ids;
  }, [spreadProposal]);
  // Most overdue first. The working list is only the rows that get a month
  // select; the rest sit under it with the reason, so the office scrolls
  // through choices, not through records it cannot fix from here.
  const spreadSorted = useMemo(
    () => spreadCandidates.slice().sort((a, b) => Number(b.monthsSince) - Number(a.monthsSince) || a.patientId.localeCompare(b.patientId)),
    [spreadCandidates],
  );
  const spreadRows = useMemo(() => spreadSorted.filter((p) => !spreadSetAside.has(p.patientId)), [spreadSorted, spreadSetAside]);
  const spreadAside = useMemo(() => spreadSorted.filter((p) => spreadSetAside.has(p.patientId)), [spreadSorted, spreadSetAside]);
  const spreadWindow = useMemo(
    () => Array.from({ length: Math.max(1, spreadMonths) }, (_, i) => addYearMonth(spreadFrom, i)),
    [spreadFrom, spreadMonths],
  );
  // The month each patient ends up in: the proposal, unless the office
  // changed that row. 'leave' keeps them where they are.
  const spreadFinal = useMemo(() => {
    const out: Record<string, string> = {};
    spreadCandidates.forEach((p) => {
      if (spreadSetAside.has(p.patientId)) return;
      const chosen = spreadEdits[p.patientId] ?? spreadProposal.assignments[p.patientId] ?? 'leave';
      out[p.patientId] = chosen;
    });
    return out;
  }, [spreadCandidates, spreadEdits, spreadProposal, spreadSetAside]);

  const openSpread = async () => {
    setSpreadEdits({});
    setSpreadPlanned({});
    setSpreadOpen(true);
    try {
      const res = await settingsApi.get('scheduling_wellness_spread_cap');
      const n = Number(res.data?.setting?.value);
      if (Number.isFinite(n) && n > 0) setSpreadCap(n);
    } catch (err) {
      // The seeded default stands.
    }
    try {
      // Twelve months is more than the dialog allows, so a change of Months
      // never needs a second fetch.
      const res = await schedulingApi.plannedCounts(spreadFrom, 12);
      setSpreadPlanned(res.data?.data || {});
    } catch (err) {
      // Without it the proposal still works; it just cannot see earlier runs.
    }
  };

  const applySpread = async () => {
    // Patients whose month is the one on screen are already listed here:
    // moving them to it changes nothing, so only the others are sent.
    const assignments = Object.entries(spreadFinal)
      .filter(([, month]) => month !== 'leave' && month !== spreadFrom)
      .map(([patientId, month]) => ({ patientId, month }));
    if (!assignments.length) { setSpreadOpen(false); return; }
    setSpreadBusy(true);
    try {
      await schedulingApi.applyWellnessPlan(assignments);
      await reloadWellness();
      setSpreadOpen(false);
      setSnackbar({
        open: true, severity: 'success',
        message: `${assignments.length} patient${assignments.length === 1 ? '' : 's'} planned for later months. Nothing was sent.`,
      });
    } catch (err: any) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.error || 'Could not apply the plan.' });
    } finally {
      setSpreadBusy(false);
    }
  };

  const handleWellnessStatus = async (patientId: string, status: string, reason?: string) => {
    setWellnessMenu(null);
    setWellnessBusy(patientId);
    try {
      await schedulingApi.setWellnessStatus(patientId, status, reason);
      await reloadWellness();
      setSnackbar({
        open: true,
        severity: 'success',
        message: status === 'dismissed'
          ? 'Removed from the recall list.'
          : status === 'not_started'
            ? 'Status cleared.'
            : 'Marked as contacted.',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || 'Could not record that.',
      });
    } finally {
      setWellnessBusy(null);
    }
  };

  // Search runs on a short delay so typing a name does not fire a query per key.
  useEffect(() => {
    if (!wellnessAddOpen || wellnessSearch.trim().length < 2) {
      setWellnessResults([]);
      return;
    }
    let cancelled = false;
    setWellnessSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await schedulingApi.searchWellnessPatients(wellnessSearch.trim());
        if (!cancelled) setWellnessResults(res.data?.data || []);
      } catch (err) {
        if (!cancelled) setWellnessResults([]);
      } finally {
        if (!cancelled) setWellnessSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [wellnessAddOpen, wellnessSearch]);

  const handleAddWellnessPatient = async (patientId: string) => {
    try {
      await schedulingApi.addWellnessPatient(patientId, format(currentDate, 'yyyy-MM'));
      setWellnessAddOpen(false);
      setWellnessSearch('');
      await reloadWellness();
      setSnackbar({ open: true, severity: 'success', message: 'Added to this month.' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || 'Could not add that patient.',
      });
    }
  };

  // Days nobody on screen is working. Driven by the providers' own schedules,
  // so a provider who starts working Saturdays opens that column by itself.
  const closedWeekdays = useMemo(
    () => nonWorkingWeekdays(providers, selectedProviders),
    [providers, selectedProviders]
  );

  // Grey those columns wherever FullCalendar draws them — the header, the day
  // cell, and the timegrid column all carry the same fc-day-xxx class.
  const closedDayStyles = useMemo(() => {
    const styles: Record<string, any> = {};
    closedWeekdays.forEach((day) => {
      const cls = WEEKDAY_CLASS[day];
      styles[`& .fc-day-${cls}`] = { backgroundColor: 'action.hover' };
      styles[`& .fc-col-header-cell.fc-day-${cls}`] = {
        backgroundColor: 'action.hover',
        '& a': { color: 'text.disabled' },
      };
      styles[`& .fc-daygrid-day.fc-day-${cls} .fc-daygrid-day-number`] = { color: 'text.disabled' };
    });
    // A cancelled appointment is struck through rather than removed: the slot
    // is free, and the practice can still see it was given up.
    styles['& .fc-event.appt-cancelled'] = {
      backgroundColor: 'transparent',
      borderStyle: 'dashed',
      opacity: 0.75,
    };
    styles['& .fc-event.appt-cancelled .fc-event-title, & .fc-event.appt-cancelled .fc-event-time'] = {
      textDecoration: 'line-through',
    };
    // A held time is not an appointment yet: a patient has been offered it and
    // may take it until the link expires. Drawn as an outline in the provider's
    // colour so the office can see why the slot is not on offer, and the name
    // says whose it is.
    styles['& .fc-event.appt-held'] = {
      backgroundColor: 'transparent',
      borderStyle: 'dotted',
      borderWidth: 2,
      opacity: 0.85,
    };
    return styles;
  }, [closedWeekdays]);

  // Update FullCalendar when date or view changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(currentDate);
      calendarApi.changeView(
        viewMode === 'month' ? 'dayGridMonth' : viewMode === 'day' ? 'timeGridDay' : 'timeGridWeek'
      );
    }
  }, [currentDate, viewMode]);

  const handleProviderToggle = (providerId: string) => {
    setSelectedProviders(prev => 
      prev.includes(providerId) 
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  // The open menu is described by an id, not by the appointment object the
  // event was mounted with: that object is a snapshot, so after cancelling one
  // the menu went on offering Cancel appointment for it.
  // Right-click on an event. Anchored to the pointer rather than an element,
  // because the thing clicked is a FullCalendar node we do not own.
  const [eventMenu, setEventMenu] = useState<{
    x: number; y: number; appointmentId: string;
  } | null>(null);

  const menuAppointment = useMemo(
    () => (eventMenu ? appointments.find(a => a.id === eventMenu.appointmentId) || null : null),
    [eventMenu, appointments],
  );

  // Open a patient's record from the wellness list, and be able to get back.
  // Same gesture the triage board uses on a task — name click goes to the full
  // profile with a labelled way back — because the team already knows it.
  //
  // The way back carries the month and the view: the wellness list only exists
  // in the month view, so returning without them would drop the doctor on this
  // week with the list gone.
  const openPatientRecord = (internalId: string) => {
    const returnTo = buildCalendarReturnTo({
      date: format(currentDate, 'yyyy-MM-dd'),
      view: viewMode as CalendarView,
    });
    navigate('/contacts', {
      state: { returnTo, backLabel: 'Back to calendar', selectInternalId: internalId },
    });
  };

  // Open the patient behind an appointment.
  //
  // The imported calendar carries a name and a phone and no patient id, so the
  // phone is the only way through. Resolved on click rather than per row: the
  // appointments table can be hundreds of rows and almost none of them get
  // clicked. Same rule as the recall list about when it is safe to open one.
  const [openingPatient, setOpeningPatient] = useState<string | null>(null);

  const openPatientForAppointment = async (apt: Appointment) => {
    const phone = String(apt.patientPhone || '').replace(/\D/g, '');
    if (phone.length < 10) {
      showSnackbar(`No phone on file for ${apt.patientName}, so there is no record to open.`, 'error');
      return;
    }
    setOpeningPatient(apt.id);
    try {
      const res = await schedulingApi.contactForPhone(apt.patientPhone as string);
      const link = resolveWellnessContact(res.data?.contactCandidates);
      if (link.internalId) {
        openPatientRecord(link.internalId);
        return;
      }
      showSnackbar(
        link.reason === 'household'
          ? `That phone belongs to a joint household record, which is not ${apt.patientName}'s chart — find them in Contacts.`
          : link.reason === 'ambiguous'
            ? `Two patients share that phone number, so the record cannot be opened from here — find ${apt.patientName} in Contacts.`
            : `No contact record matches ${apt.patientName}'s phone number.`,
        'error',
      );
    } catch (err) {
      console.error('Failed to look up the patient for an appointment:', err);
      showSnackbar('Could not look up that patient. Please try again.', 'error');
    } finally {
      setOpeningPatient(null);
    }
  };

  // Destructive actions on this page ask first, through a dialog we render
  // ourselves. window.confirm looked simpler and was, until a browser declined
  // to show it: a suppressed confirm returns false, the code read that as "the
  // user said no", and Cancel appointment did nothing at all — no error, no
  // message, appointment still blocking its slot.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    body: React.ReactNode;
    confirmLabel: string;
    run: () => Promise<void>;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const runPendingConfirm = async () => {
    if (!pendingConfirm) return;
    setConfirmBusy(true);
    try {
      await pendingConfirm.run();
      setPendingConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  // One cancel for the whole page: the Appointments table, the mobile cards,
  // the right-click menu, and the edit dialog. It was written out three times
  // otherwise, and the third copy is where they start to disagree.
  const requestCancelAppointment = (apt: Appointment, onCancelled?: () => void) => {
    notifyPatientRef.current = false;
    setNotifyPatient(false);
    setPendingConfirm({
      title: 'Cancel this appointment?',
      confirmLabel: 'Cancel appointment',
      body: (
        <>
          <DialogContentText>
            {describeAppointment(apt).replace(/^for /, '')} will be cancelled and the slot
            becomes free for someone else.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={
              <Checkbox
                size="small"
                defaultChecked={false}
                onChange={(e) => {
                  notifyPatientRef.current = e.target.checked;
                  setNotifyPatient(e.target.checked);
                }}
                sx={{ pt: 0.25 }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Also open a follow-up</Typography>
                <Typography variant="caption" color="text.secondary">
                  A text goes out on its own telling them this visit is off. Tick this to also
                  open a follow-up on their task, with a link to pick a new time when the
                  practice allows self-booking. That follow-up is not sent until you press Send.
                </Typography>
              </Box>
            }
          />
        </>
      ),
      run: async () => {
        try {
          const updated = await schedulingApi.updateAppointment(apt.id, { status: 'cancelled' });
          const patientText = updated?.data?.data?.patientText;
          const textFailed = Boolean(patientText) && !patientText.sent;
          setAppointments(prev => prev.map(a => (a.id === apt.id ? { ...a, status: 'cancelled' } : a)));
          await fetchAppointments();

          // The visit is already off the calendar. The text is reported here.
          // The checkbox only opens a follow-up draft.
          if (notifyPatientRef.current) {
            try {
              const res = await schedulingLinksApi.cancellationNotice(apt.id, true);
              const data = res.data?.data || res.data || {};
              const message = cancellationNotice({
                patientName: data.patientName || apt.patientName || '',
                // Built from the timestamp, not sliced out of a sentence. A
                // regex over "Joel Schwartz on 09/08/2026" left the patient's
                // own name inside their cancellation message.
                when: practiceDateTimeLabel(data.startTime || apt.startTime),
                bookingUrl: data.bookingUrl || undefined,
              });
              if (data.taskId) {
                navigate(`/?taskId=${data.taskId}&draft=${encodeURIComponent(message)}`);
                const followUp = patientText?.sent
                  ? 'Cancelled, and the patient was texted. A follow-up is waiting on their task — press Send there.'
                  : patientText
                    ? `Cancelled. ${patientTextSuffix(patientText)} A follow-up is waiting on their task — press Send there.`
                    : 'Cancelled. A follow-up is waiting on their task — press Send there.';
                showSnackbar(followUp, textFailed ? 'error' : 'success');
              } else {
                showSnackbar(
                  `Cancelled, but ${apt.patientName || 'this patient'} has no message thread, so there is nowhere to draft a follow-up. ${patientTextSuffix(patientText)}`.trim(),
                  textFailed ? 'error' : 'success',
                );
              }
              return;
            } catch (noticeError: any) {
              const noticeStatus = noticeError?.response?.status;
              if (noticeStatus === 422) {
                showSnackbar(
                  'This appointment did not have a visible phone number attached, so the patient was not messaged but the appointment has been cancelled; please notify patient otherwise',
                  'error',
                );
              } else {
                showSnackbar(
                  `Cancelled, but the follow-up could not be written. ${patientTextSuffix(patientText)}`.trim(),
                  'error',
                );
              }
              return;
            }
          }
          const told = patientTextSuffix(patientText);
          showSnackbar(
            `Appointment cancelled ${describeAppointment(apt)} — the slot is free again.${told ? ' ' + told : ''}`,
            textFailed ? 'error' : 'success',
          );
          onCancelled?.();
        } catch (error) {
          console.error('Failed to cancel appointment:', error);
          showSnackbar('Could not cancel the appointment. Please try again.', 'error');
        }
      },
    });
  };

  const requestDeleteAppointment = (apt: Appointment) => {
    setPendingConfirm({
      title: 'Delete this appointment?',
      confirmLabel: 'Delete permanently',
      body: (
        <DialogContentText>
          {describeAppointment(apt).replace(/^for /, '')} will be removed for good. To free the
          slot but keep the record, cancel it instead.
        </DialogContentText>
      ),
      run: async () => {
        try {
          await schedulingApi.deleteAppointment(apt.id);
          setAppointments(prev => prev.filter(a => a.id !== apt.id));
          await fetchAppointments();
          showSnackbar(`Appointment deleted ${describeAppointment(apt)} — the slot is free again.`);
        } catch (error) {
          console.error('Failed to delete appointment:', error);
          showSnackbar('Could not delete the appointment. Please try again.', 'error');
        }
      },
    });
  };

  const undoWellnessScheduling = async (taskId: string) => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    try {
      await triageApi.deleteTask(taskId);
      await reloadWellness();
      showSnackbar('Scheduling task removed. The patient is still due.');
    } catch (err) {
      console.error('Failed to undo the scheduling task:', err);
      showSnackbar('Could not undo that. The task is on the dashboard.', 'error');
    }
  };

  const handleNewAppointment = () => {
    setSelectedAppointment(null);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setDuplicateSource(null);
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  // Duplicate opens a NEW appointment carrying this one's details, rather than
  // writing a copy straight away: the copy would want the slot the original
  // already holds, so a blind duplicate always collides. selectedAppointment
  // stays null so the save path creates rather than updates.
  const handleDuplicateAppointment = (appointment: Appointment) => {
    setSelectedAppointment(null);
    setDuplicateSource(appointment);
    setIsModalOpen(true);
  };

  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode);
  };

  const handleDateChange = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'today') {
      setCurrentDate(new Date());
    } else if (direction === 'prev') {
      if (viewMode === 'month') {
        setCurrentDate(prev => subMonths(prev, 1));
      } else if (viewMode === 'week') {
        setCurrentDate(prev => subWeeks(prev, 1));
      } else {
        setCurrentDate(prev => subDays(prev, 1));
      }
    } else {
      if (viewMode === 'month') {
        setCurrentDate(prev => addMonths(prev, 1));
      } else if (viewMode === 'week') {
        setCurrentDate(prev => addWeeks(prev, 1));
      } else {
        setCurrentDate(prev => addDays(prev, 1));
      }
    }
  };

  const handleDatePickerOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      handleDatePickerClose();
    }
  };

  // Get date range label based on view mode
  const getDateRangeLabel = (): string => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy');
    }
    if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
      return `${formatDateOnly(weekStart, dateTimeFormat)} - ${formatDateOnly(weekEnd, dateTimeFormat)}`;
    }
    return formatDateOnly(currentDate, dateTimeFormat);
  };

  // Filter appointments by date range based on view mode
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      // Status filter
      if (statusFilter !== 'all' && apt.status !== statusFilter) return false;
      
      // Provider filter. The rule — including what an empty tick list means —
      // lives in calendarProviderFilter so it can be tested on its own.
      if (!matchesProviderFilter(providers, selectedProviders, apt.provider)) return false;
      
      // Date filter based on view mode
      try {
        const aptDate = parseLocalTime(apt.startTime);
        
        if (viewMode === 'list') {
          // List view: show appointments for the selected day only
          return isSameDay(aptDate, currentDate);
        } else if (viewMode === 'day') {
          // Day view: show appointments for the selected day
          return isSameDay(aptDate, currentDate);
        } else if (viewMode === 'week') {
          // Week view: show appointments for the selected week
          const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
          const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
          return isWithinInterval(aptDate, { start: weekStart, end: weekEnd });
        } else if (viewMode === 'month') {
          return isWithinInterval(aptDate, {
            start: startOfMonth(currentDate),
            end: endOfMonth(currentDate),
          });
        }
      } catch (e) {
        console.error('Error parsing date:', apt.startTime, e);
        return false;
      }
      
      return true;
    });
  }, [appointments, statusFilter, selectedProviders, providers, currentDate, viewMode]);

  // How much of the day to draw. Derived rather than fixed, so the grid is the
  // practice's day instead of 05:00-21:00 with the middle third used — and it
  // widens for anything booked outside those hours rather than hiding it.
  const timeWindow = useMemo(
    () => gridTimeWindow(
      providers as any,
      filteredAppointments
        .map((apt) => ({ start: parseLocalTime(apt.startTime), end: parseLocalTime(apt.endTime) }))
        .filter((a) => a.start instanceof Date && !Number.isNaN(a.start.getTime())),
    ),
    [providers, filteredAppointments],
  );


  // Sort appointments by time for list view
  const sortedAppointments = useMemo(() => {
    return [...filteredAppointments].sort((a, b) => {
      const dateA = parseLocalTime(a.startTime).getTime();
      const dateB = parseLocalTime(b.startTime).getTime();
      return dateA - dateB;
    });
  }, [filteredAppointments]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      scheduled: '#3b82f6',
      confirmed: '#10b981',
      pending_arrival: '#f59e0b',
      in_lobby: '#8b5cf6',
      in_room: '#06b6d4',
      completed: '#10b981',
      cancelled: '#ef4444',
      no_show: '#6b7280',
      tentative: '#f59e0b',
    };
    return colors[status] || '#6b7280';
  };

  const formatStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      pending_arrival: 'Pending Arrival',
      in_lobby: 'In Lobby',
      in_room: 'In Room',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No Show',
      tentative: 'Tentative',
    };
    return labels[status] || status;
  };

  const isDatePickerOpen = Boolean(datePickerAnchor);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header — density pass: h5 title, mb 3→2 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Calendar | Scheduling
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage appointments and schedule
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Times are pinned to the clinic clock, so they are right wherever
                this is opened. Said out loud only when the machine disagrees —
                otherwise it is noise. */}
            {viewerIsElsewhere() && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`Times shown in practice time (${practiceTimeLabel()})`}
                title={`Your computer is set to ${Intl.DateTimeFormat().resolvedOptions().timeZone}. Appointment times are shown in ${practiceZone} so they match the clinic.`}
              />
            )}
            {taskReturn && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(taskReturn.to)}
              >
                {taskReturn.label}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={() => setIsQuickScheduleOpen(true)}
            >
              Quick Schedule
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewAppointment}
            >
              New Appointment
            </Button>
          </Box>
        </Box>

        {/* View Mode Tabs & Filters */}
        <Paper sx={{ p: { xs: 1, sm: 1.25 }, mb: { xs: 1.25, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            {/* View Mode Tabs */}
            {/* On a phone these four shared one row at MUI's default 90px per
                tab — 396px of tabs in a 331px strip, so Month sat off the end
                with no scroll buttons to reach it. Full width divides the row
                between them instead, and the list view is "List" there because
                "Appointments" is what pushed the row over. */}
            <Tabs
              value={viewMode}
              onChange={(_, value) => handleViewModeChange(value)}
              variant={isMobile ? 'fullWidth' : 'standard'}
              sx={{
                minHeight: { xs: 36, sm: 40 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: { xs: 0, sm: 90 },
                  minHeight: { xs: 36, sm: 48 },
                  px: { xs: 0.5, sm: 2 },
                  py: { xs: 0.5, sm: 1.5 },
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                },
              }}
            >
              <Tab label={isMobile ? 'List' : 'Appointments'} value="list" />
              <Tab label="Day" value="day" />
              <Tab label="Week" value="week" />
              <Tab label="Month" value="month" />
            </Tabs>

            {/* Date Navigation */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.25, sm: 1 },
                flexWrap: 'nowrap',
                // Sized to content on a phone so the Filters button can sit on
                // the same line; given the slack on wider screens.
                flexGrow: { xs: 0, sm: 1 },
                minWidth: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleDateChange('prev')}
                sx={{ p: { xs: 0.25, sm: 0.625 } }}
              >
                <PrevIcon />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                // The icon is decoration next to a button that already says
                // "Today", and dropping it on a phone is the 24px that lets
                // Filters share this row instead of taking one of its own.
                startIcon={isMobile ? undefined : <TodayIcon />}
                onClick={() => handleDateChange('today')}
              >
                Today
              </Button>
              <IconButton
                size="small"
                onClick={() => handleDateChange('next')}
                sx={{ p: { xs: 0.25, sm: 0.625 } }}
              >
                <NextIcon />
              </IconButton>
              
              {/* Clickable Date Label with Calendar Dropdown */}
              <Button
                variant="text"
                size="small"
                onClick={handleDatePickerOpen}
                endIcon={<CalendarIcon fontSize="small" />}
                sx={{
                  ml: { xs: 0, sm: 1 },
                  px: { xs: 0.25, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  color: 'text.primary',
                  // A fixed 180px pushed the row over on a phone and sent the
                  // Filters button to a line of its own.
                  minWidth: { xs: 0, sm: 180 },
                  flexShrink: 1,
                  justifyContent: 'flex-start',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  '& .MuiButton-endIcon': { ml: { xs: 0.25, sm: 1 } },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                {isMobile ? compactRangeLabel(currentDate, viewMode) : getDateRangeLabel()}
              </Button>

              {/* Date Picker Popover */}
              <Popover
                open={isDatePickerOpen}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <DateCalendar
                  value={currentDate}
                  onChange={handleDateSelect}
                  sx={{ 
                    '& .MuiPickersDay-root.Mui-selected': {
                      backgroundColor: 'primary.main',
                    }
                  }}
                />
              </Popover>
            </Box>

            {/* Status Filter — inline on desktop; folds into the mobile
                Filters collapse below. */}
            {!isMobile && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Providers — a collapsed multi-select beside Status rather than
                its own sidebar column. Two checkboxes did not earn 200px of
                width next to a month grid, and the column stood empty below
                them. Mobile keeps them in the Filters collapse below. */}
            {!isMobile && (
              <FormControl size="small" sx={{ minWidth: 190 }}>
                {/* shrink + displayEmpty: with nothing ticked MUI skips
                    renderValue and paints a zero-width space, so the control
                    looked broken in exactly the state where the calendar is
                    showing every provider. */}
                <InputLabel shrink>Providers</InputLabel>
                <Select
                  multiple
                  displayEmpty
                  notched
                  value={selectedProviders}
                  label="Providers"
                  onChange={(e) => {
                    const next = e.target.value;
                    setSelectedProviders(typeof next === 'string' ? next.split(',') : next);
                  }}
                  renderValue={() => summarizeProviderSelection(providers, selectedProviders)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 340 } } }}
                >
                  {providers.map(provider => (
                    <MenuItem key={provider.id} value={provider.id} sx={{ py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={selectedProviders.includes(provider.id)}
                        sx={{
                          mr: 0.5,
                          color: providerColorMap[provider.id] || '#3b82f6',
                          '&.Mui-checked': { color: providerColorMap[provider.id] || '#3b82f6' },
                        }}
                      />
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          mr: 1,
                          bgcolor: providerColorMap[provider.id] || '#3b82f6',
                        }}
                      />
                      <Typography variant="body2" noWrap>{provider.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Mobile: a single Filters toggle (Status + Providers live in the
                collapse) with an active-filter count badge. */}
            {isMobile && (
              <Badge
                color="primary"
                badgeContent={
                  (statusFilter !== 'all' ? 1 : 0) +
                  (providers.length > 0 && selectedProviders.length < providers.length ? 1 : 0)
                }
              >
                <Button
                  size="small"
                  variant={mobileFiltersOpen ? 'contained' : 'outlined'}
                  startIcon={<FilterIcon />}
                  onClick={() => setMobileFiltersOpen((v) => !v)}
                >
                  Filters
                </Button>
              </Badge>
            )}
          </Box>

          {/* Mobile: collapsible Status + Providers */}
          {isMobile && (
            <Collapse in={mobileFiltersOpen}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.25, pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Providers
                  </Typography>
                  <FormGroup>
                    {providers.map(provider => (
                      <FormControlLabel
                        key={provider.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedProviders.includes(provider.id)}
                            onChange={() => handleProviderToggle(provider.id)}
                            sx={{ color: providerColorMap[provider.id] || '#3b82f6', '&.Mui-checked': { color: providerColorMap[provider.id] || '#3b82f6' } }}
                          />
                        }
                        label={provider.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Box>
            </Collapse>
          )}
        </Paper>

        {/* An empty calendar because nobody is ticked looks exactly like an
            empty calendar because nothing is booked. Say which it is, and
            offer the way back — the filter is two clicks away in a collapsed
            control on desktop and behind Filters on a phone. */}
        {providers.length > 0 && selectedProviders.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: { xs: 1.25, md: 2 } }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setSelectedProviders(providers.map(provider => provider.id))}
              >
                Show all
              </Button>
            }
          >
            No providers are selected, so no appointments are shown.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: { xs: 1.25, md: 2 }, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', alignItems: { xs: 'stretch', md: 'flex-start' } }}>
          {/* Provider Filter Sidebar — desktop only; on mobile the providers
              live in the Filters collapse above. */}
          {viewMode === 'month' && (
            <Paper
              sx={{
                p: { xs: 1.25, md: 1.75 },
                // Wide enough for a name and its actions on one line. Full
                // width on a phone, where this is the reason the month view is
                // open at all.
                width: { xs: '100%', md: 'auto' },
                flex: { xs: 'none', md: '1 1 360px' },
                maxWidth: { md: 460 },
                minWidth: 0,
                alignSelf: { xs: 'stretch', md: 'flex-start' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Wellness due
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  {format(currentDate, 'MMMM yyyy')}
                </Typography>
              </Box>
              {wellnessLoading ? (
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              ) : wellnessDue.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nobody is due this month.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {wellnessDue.length} patient{wellnessDue.length === 1 ? '' : 's'}
                      {wellnessDue.some(p => p.overdue) && `, ${wellnessDue.filter(p => p.overdue).length} overdue`}
                    </Typography>
                    {/* Only when there is a backlog to spread. Proposes; the
                        office reads the proposal and applies it, or not. */}
                    {wellnessDue.some(p => !p.addedByHand && Number(p.monthsSince) >= 18 && p.status === 'not_started' && !p.taskId) && (
                      <Tooltip title="Spreads the patients 18 months or more overdue across the coming months, most overdue first, so they are not all this month's work. You see the plan before anything changes; nothing is sent.">
                        <Button size="small" startIcon={<SpreadIcon fontSize="small" />} sx={{ textTransform: 'none', ml: 'auto' }} onClick={openSpread}>
                          Suggest a spread
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ maxHeight: { xs: 340, md: 560 }, overflowY: 'auto', pr: 0.5 }}>
                    {wellnessDue.map((patient, index) => {
                      // A heading each time the list crosses from the patients
                      // whose month this is into the ones already past it.
                      const previous = wellnessDue[index - 1];
                      const startsGroup = index === 0 || previous.dueThisMonth !== patient.dueThisMonth;
                      // Whether this row may open a chart, and if not, why not.
                      const patientLink = resolveWellnessContact(patient.contactCandidates);
                      // What this row has got to, and what its button does.
                      const rowState = describeRecallRow(patient);
                      const groupLabel = patient.dueThisMonth
                        ? `Due this month (${wellnessDue.filter(p => p.dueThisMonth).length})`
                        : `Already overdue (${wellnessDue.filter(p => !p.dueThisMonth).length})`;
                      return (
                        <React.Fragment key={patient.patientId}>
                          {startsGroup && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', mt: index === 0 ? 0 : 1.5, mb: 0.5, fontWeight: 700, color: 'text.secondary' }}
                            >
                              {groupLabel}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              py: 0.75,
                              opacity: wellnessBusy === patient.patientId ? 0.5 : 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <Tooltip
                                title={
                                  patientLink.internalId
                                    ? `Open this patient's record — you can come straight back`
                                    : patientLink.reason === 'household'
                                      ? 'This phone belongs to a joint household record, which is not this patient\u2019s chart — find them in Contacts'
                                      : patientLink.reason === 'ambiguous'
                                        ? 'Two patients share this phone number, so the record cannot be opened from here — find them in Contacts'
                                        : 'No contact record matches this phone number'
                                }
                              >
                                <Typography
                                  component={patientLink.internalId ? 'button' : 'span'}
                                  type={patientLink.internalId ? 'button' : undefined}
                                  variant="body2"
                                  onClick={patientLink.internalId
                                    ? () => openPatientRecord(patientLink.internalId as string)
                                    : undefined}
                                  sx={{
                                    fontWeight: 600,
                                    flexGrow: 1,
                                    minWidth: 0,
                                    // Imported records include long machine
                                    // names; one clipped line beats three.
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    // Reads as a link only when it is one; the
                                    // rest stay plain text rather than offering
                                    // a click that cannot be honoured.
                                    display: 'block',
                                    textAlign: 'left',
                                    border: 0,
                                    p: 0,
                                    background: 'none',
                                    font: 'inherit',
                                    ...(patientLink.internalId
                                      ? {
                                          color: 'primary.main',
                                          cursor: 'pointer',
                                          '&:hover, &:focus-visible': { textDecoration: 'underline' },
                                        }
                                      : { color: 'text.primary' }),
                                  }}
                                >
                                  {patient.patientName}
                                </Typography>
                              </Tooltip>
                              <Tooltip title={rowState.hint}>
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={patient.taskId
                                      ? `Open the scheduling task for ${patient.patientName}`
                                      : `Start scheduling for ${patient.patientName}`}
                                    color={patient.taskId ? 'primary' : 'default'}
                                    disabled={rowState.action === 'blocked' || wellnessBusy === patient.patientId}
                                    onClick={() => patient.taskId
                                      // The dashboard's ?taskId= deep link opens
                                      // that task's dock, so the button lands on
                                      // the work it just made rather than on a
                                      // board of 500 to search through.
                                      ? navigate(`/?taskId=${patient.taskId}`)
                                      : handleStartWellnessScheduling(patient.patientId)}
                                  >
                                    {patient.taskId
                                      ? <OpenTaskIcon fontSize="small" />
                                      : <ScheduleSendIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <IconButton
                                size="small"
                                aria-label={`More actions for ${patient.patientName}`}
                                onClick={(e) => setWellnessMenu({ el: e.currentTarget, patientId: patient.patientId })}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                              <Typography variant="caption" color="text.secondary">
                                {patient.lastWellness
                                  ? `last seen ${formatDateOnly(new Date(patient.lastWellness), dateTimeFormat)}`
                                  : 'added by hand — no wellness on record'}
                              </Typography>
                              {patient.overdue && (
                                <Chip
                                  size="small"
                                  label={`${patient.monthsSince} mo`}
                                  color="warning"
                                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                                />
                              )}
                              {patient.plannedThisMonth && (
                                <Tooltip title={`The office planned this patient for ${format(currentDate, 'MMMM')}. Their status is unchanged.`}>
                                  <Chip
                                    size="small"
                                    label="planned"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                                  />
                                </Tooltip>
                              )}
                              {patient.encounterSource === 'pf_import' && (
                                <Tooltip title="The last-seen date comes from the Practice Fusion import, which has not been checked against the chart. Treat the months-overdue figure as approximate.">
                                  <Chip
                                    size="small"
                                    label="imported"
                                    variant="outlined"
                                    sx={{ height: 18, color: 'text.secondary', '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                                  />
                                </Tooltip>
                              )}
                              {patient.status === 'outreach_started' && (
                                <Chip
                                  size="small"
                                  // A task exists. Nobody has spoken to the patient
                                  // yet, and the chip must not imply otherwise.
                                  label="task started"
                                  color="info"
                                  variant="outlined"
                                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                                />
                              )}
                              {patient.status === 'contacted' && (
                                <Chip
                                  size="small"
                                  label="contacted"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                                />
                              )}
                            </Box>
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Box>

                  {wellnessAddOpen ? (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Name or phone…"
                        value={wellnessSearch}
                        onChange={(e) => setWellnessSearch(e.target.value)}
                      />
                      {wellnessSearching && (
                        <Typography variant="caption" color="text.secondary">Searching…</Typography>
                      )}
                      {!wellnessSearching && wellnessSearch.trim().length >= 2 && wellnessResults.length === 0 && (
                        <Typography variant="caption" color="text.secondary">No patient by that name.</Typography>
                      )}
                      <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                        {wellnessResults.map(result => (
                          <Box
                            key={result.patientId}
                            onClick={() => handleAddWellnessPatient(result.patientId)}
                            sx={{
                              py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 1,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Typography variant="body2">{result.patientName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {result.patientPhone || 'no phone on file'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Button size="small" sx={{ textTransform: 'none' }}
                        onClick={() => { setWellnessAddOpen(false); setWellnessSearch(''); }}>
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Tooltip
                      placement="top-start"
                      title="For someone the list missed. Wellness visits are recognised from visit notes, so the match has gaps. Also the way to put back a patient you removed."
                    >
                      <Button
                        size="small"
                        startIcon={<AddIcon fontSize="small" />}
                        sx={{ textTransform: 'none', mt: 1 }}
                        onClick={() => setWellnessAddOpen(true)}
                      >
                        Add a patient
                      </Button>
                    </Tooltip>
                  )}

                  <Menu
                    open={Boolean(wellnessMenu)}
                    anchorEl={wellnessMenu?.el}
                    onClose={() => setWellnessMenu(null)}
                  >
                    {/* Each item says what it does and, where it matters, what
                        it does not — these are easy to confuse and two of them
                        change what the office sees tomorrow. */}
                    <Tooltip
                      placement="right"
                      title="Records that someone has reached out. The patient stays on the list with a contacted mark. Nothing is sent to them."
                    >
                      <MenuItem onClick={() => wellnessMenu && handleWellnessStatus(wellnessMenu.patientId, 'contacted')}>
                        Mark contacted
                      </MenuItem>
                    </Tooltip>
                    <Tooltip
                      placement="right"
                      title="Removes the contacted or task-started mark. The patient stays on the list."
                    >
                      <MenuItem onClick={() => wellnessMenu && handleWellnessStatus(wellnessMenu.patientId, 'not_started')}>
                        Clear status
                      </MenuItem>
                    </Tooltip>
                    <Tooltip
                      placement="right"
                      title="Puts this patient on a later month's list instead of this one. Their status is untouched and nothing is sent; if nothing happens by then, they come back as overdue."
                    >
                      <MenuItem onClick={() => {
                        if (!wellnessMenu) return;
                        setWellnessMoveMonth(addYearMonth(format(currentDate, 'yyyy-MM'), 1));
                        setWellnessMoveFor(wellnessMenu.patientId);
                        setWellnessMenu(null);
                      }}>
                        Move to month…
                      </MenuItem>
                    </Tooltip>
                    {(() => {
                      const row = wellnessDue.find(p => p.patientId === wellnessMenu?.patientId);
                      return row?.dueMonth && !row.addedByHand;
                    })() && (
                      <Tooltip placement="right" title="Forgets the planned month. The patient is listed wherever their visit history puts them.">
                        <MenuItem onClick={() => wellnessMenu && handleMoveWellnessMonth(wellnessMenu.patientId, null)}>
                          Clear planned month
                        </MenuItem>
                      </Tooltip>
                    )}
                    {/* Only offered where it does something Clear status does
                        not: taking a hand-added patient back off the month. On
                        a patient found from their own history the two were
                        indistinguishable. */}
                    {wellnessDue.find(p => p.patientId === wellnessMenu?.patientId)?.addedByHand && (
                      <Tooltip
                        placement="right"
                        title="Takes a patient you added by hand back off this month. Their record and visit history are untouched."
                      >
                        <MenuItem onClick={() => wellnessMenu && handleUndoWellnessRecord(wellnessMenu.patientId)}>
                          Remove from this month
                        </MenuItem>
                      </Tooltip>
                    )}
                    <Tooltip
                      placement="right"
                      title="Takes them off the recall list — they left the practice, declined, or are not due. They stop appearing in later months too. To put them back, use Add a patient."
                    >
                      <MenuItem
                        onClick={() => wellnessMenu && handleWellnessStatus(wellnessMenu.patientId, 'dismissed', 'removed from the recall list')}
                        sx={{ color: 'error.main' }}
                      >
                        Not due — remove
                      </MenuItem>
                    </Tooltip>
                  </Menu>

                  {/* Move one patient. A month picker and a sentence. */}
                  <Dialog open={Boolean(wellnessMoveFor)} onClose={() => setWellnessMoveFor(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Move to a month</DialogTitle>
                    <DialogContent>
                      <DialogContentText sx={{ mb: 2 }}>
                        {wellnessDue.find(p => p.patientId === wellnessMoveFor)?.patientName || 'This patient'} will be listed in the month you choose instead of this one. Nothing is sent, and their status does not change.
                      </DialogContentText>
                      <TextField
                        type="month"
                        label="Month"
                        size="small"
                        fullWidth
                        value={wellnessMoveMonth}
                        onChange={(e) => setWellnessMoveMonth(e.target.value)}
                        inputProps={{ min: format(currentDate, 'yyyy-MM'), 'aria-label': 'Month to plan this patient for' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setWellnessMoveFor(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                      <Button
                        variant="contained"
                        disabled={!/^\d{4}-\d{2}$/.test(wellnessMoveMonth)}
                        onClick={() => wellnessMoveFor && handleMoveWellnessMonth(wellnessMoveFor, wellnessMoveMonth)}
                        sx={{ textTransform: 'none' }}
                      >
                        Move
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* The suggested spread. Proposal first, every row editable,
                      applied only on the button. Full screen on a phone. */}
                  <Dialog open={spreadOpen} onClose={() => !spreadBusy && setSpreadOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
                    <DialogTitle>Suggest a spread</DialogTitle>
                    <DialogContent>
                      <DialogContentText sx={{ mb: 2 }}>
                        Patients 18 months or more overdue with nothing done yet, most overdue first, filling {format(currentDate, 'MMMM')} to the cap and then the months after. Change any row before you apply. Nothing is sent.
                      </DialogContentText>
                      {/* Two short fields with matching helper lines so they sit
                          level, then the checkbox on its own line. Wraps as two
                          equal halves on a phone. */}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 0.5 }}>
                        <TextField
                          type="number" label="Months" size="small" value={spreadMonths}
                          onChange={(e) => setSpreadMonths(Math.min(12, Math.max(1, parseInt(e.target.value) || 1)))}
                          inputProps={{ min: 1, max: 12, 'aria-label': 'Months to spread over' }}
                          helperText="1 to 12"
                          sx={{ flex: { xs: 1, sm: '0 0 120px' } }}
                        />
                        <TextField
                          type="number" label="Per month" size="small" value={spreadCap}
                          onChange={(e) => setSpreadCap(Math.max(1, parseInt(e.target.value) || 1))}
                          inputProps={{ min: 1, 'aria-label': 'Patients per month' }}
                          helperText="Set in Settings"
                          sx={{ flex: { xs: 1, sm: '0 0 120px' } }}
                        />
                      </Box>
                      <FormControlLabel
                        sx={{ mx: 0, mb: 1, alignItems: 'flex-start' }}
                        control={<Checkbox size="small" sx={{ py: 0.25, mr: 0.5 }} checked={spreadIncludeImported} onChange={(e) => { setSpreadIncludeImported(e.target.checked); setSpreadEdits({}); }} />}
                        label={<Typography variant="body2" sx={{ pt: 0.25 }}>Include patients whose last visit is from the import ({spreadImportedCount})</Typography>}
                      />
                      {spreadWindow.slice(1).some((m) => (spreadPlanned[m] || 0) > 0) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {spreadWindow.slice(1).filter((m) => (spreadPlanned[m] || 0) > 0)
                            .map((m) => `${spreadPlanned[m]} already planned for ${format(new Date(`${m}-15T12:00:00`), 'MMMM')} from an earlier run`)
                            .join(' · ')}.
                        </Typography>
                      )}
                      {spreadRows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Nobody to spread with these choices.</Typography>
                      ) : (
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: { xs: 'none', sm: 360 }, overflowY: 'auto' }}>
                          {spreadRows.map((p) => (
                            <Box key={p.patientId} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" noWrap>{p.patientName}</Typography>
                                <Typography variant="caption" color="text.secondary">{p.monthsSince} mo overdue</Typography>
                              </Box>
                              <Select
                                size="small"
                                value={spreadFinal[p.patientId] || 'leave'}
                                onChange={(e) => setSpreadEdits((prev) => ({ ...prev, [p.patientId]: String(e.target.value) }))}
                                inputProps={{ 'aria-label': `Month for ${p.patientName}` }}
                                sx={{ minWidth: { xs: 132, sm: 150 }, fontSize: '0.85rem' }}
                              >
                                {spreadWindow.map((m) => (
                                  <MenuItem key={m} value={m}>{format(new Date(`${m}-15T12:00:00`), 'MMMM yyyy')}</MenuItem>
                                ))}
                                <MenuItem value="leave">Leave on the list</MenuItem>
                              </Select>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {/* Rows with nothing to choose: already planned, or the
                          practice cannot reach them until the record is fixed.
                          Folded, so the list above stays a list of decisions —
                          but one click shows who needs a phone or a name. */}
                      {spreadAside.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Button
                            size="small"
                            onClick={() => setSpreadAsideOpen((v) => !v)}
                            aria-expanded={spreadAsideOpen}
                            sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}
                          >
                            {spreadAsideOpen ? 'Hide' : 'Show'} {spreadAside.length} not in this spread
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {[
                              spreadProposal.leftOut.noPhone.length ? `${spreadProposal.leftOut.noPhone.length} with no phone on file` : '',
                              spreadProposal.leftOut.noName.length ? `${spreadProposal.leftOut.noName.length} with no usable name` : '',
                              spreadProposal.kept.length ? `${spreadProposal.kept.length} already planned for ${format(currentDate, 'MMMM')}` : '',
                            ].filter(Boolean).join(' · ')}.
                          </Typography>
                          <Collapse in={spreadAsideOpen}>
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: { xs: 'none', sm: 200 }, overflowY: 'auto' }}>
                              {spreadAside.map((p) => (
                                // Name on one line, reason and actions on the next
                                // at phone width; one line of everything wider.
                                <Box key={p.patientId} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.25, sm: 1 }, px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary" noWrap>{p.patientName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{p.monthsSince} mo overdue</Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, textAlign: 'right' }}>
                                    {spreadProposal.kept.includes(p.patientId) ? `Already planned for ${format(currentDate, 'MMMM')}`
                                      : spreadProposal.leftOut.noPhone.includes(p.patientId) ? 'No phone on file'
                                      : 'No usable name'}
                                  </Typography>
                                  {/* The fix lives in the record, so the record is one
                                      click away; so is taking them off the list. Not
                                      offered on a planned row — nothing is wrong there. */}
                                  {!spreadProposal.kept.includes(p.patientId) && (() => {
                                    const link = resolveWellnessContact(p.contactCandidates);
                                    return (
                                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        <Tooltip title={link.internalId ? 'Open this patient\'s record to add what is missing — you can come straight back' : 'No record found for this patient'}>
                                          <span>
                                            <Button
                                              size="small"
                                              disabled={!link.internalId}
                                              onClick={() => link.internalId && openPatientRecord(link.internalId)}
                                              sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                                            >
                                              Open record
                                            </Button>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Takes them off the recall list — they left the practice, declined, or are not due. To put them back, use Add a patient.">
                                          <span>
                                            <Button
                                              size="small"
                                              color="error"
                                              disabled={wellnessBusy === p.patientId}
                                              onClick={() => handleWellnessStatus(p.patientId, 'dismissed', 'removed from the recall list')}
                                              sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}
                                            >
                                              Remove
                                            </Button>
                                          </span>
                                        </Tooltip>
                                      </Box>
                                    );
                                  })()}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                      {spreadProposal.overflow.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {spreadProposal.overflow.length} more than {spreadMonths} month{spreadMonths === 1 ? '' : 's'} at {spreadCap} a month can take — they stay on the list. Add months or raise the cap to place them.
                        </Typography>
                      )}
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setSpreadOpen(false)} disabled={spreadBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
                      <Button variant="contained" onClick={applySpread} disabled={spreadBusy || spreadRows.length === 0} sx={{ textTransform: 'none' }}>
                        {spreadBusy ? 'Applying…' : `Apply — move ${Object.values(spreadFinal).filter((m) => m !== 'leave' && m !== spreadFrom).length}`}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </Paper>
          )}


          {/* Calendar Content */}
          {/* minWidth:0 is load-bearing: flex items default to min-width:auto and
              refuse to shrink below their content, so a wide table pushed this
              column past the viewport (Actions column clipped) instead of
              letting it scroll — and it got worse as the sidebar expanded. */}
          <Paper sx={{
            // A floor so the grid drops to its own full-width line rather than
            // being crushed to a sliver when the recall list is beside it.
            flex: { xs: '1 1 100%', md: '1 1 420px' },
            p: { xs: 1, sm: 1.5 },
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
          }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {viewMode === 'list' && (
                  <>
                    {sortedAppointments.length === 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 300,
                        color: 'text.secondary'
                      }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          No appointments for {formatDateOnly(currentDate, dateTimeFormat)}
                        </Typography>
                        <Typography variant="body2">
                          Click on the date above to jump to a different date, or use the arrows to navigate.
                        </Typography>
                      </Box>
                    ) : isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {sortedAppointments.map((apt) => {
                          const aptDate = parseLocalTime(apt.startTime);
                          const isToday = isSameDay(aptDate, new Date());
                          const duration = editorDuration(apt);
                          return (
                            <MobileRecordCard
                              key={apt.id}
                              title={apt.patientName}
                              subtitle={[
                                apt.patientPhone,
                                apt.patientDOB ? formatDateOnly(apt.patientDOB, dateTimeFormat) : null,
                                apt.patientGender,
                              ].filter(Boolean).join(' • ')}
                              rows={[
                                {
                                  label: 'Status',
                                  // Cancelled is terminal — the workflow Select
                                  // (awaiting arrival -> in room -> completed) doesn't
                                  // apply to it, and it has no 'cancelled' option, so it
                                  // rendered blank. Show the status chip instead.
                                  value: isToday && apt.status !== 'cancelled' ? (
                                    <Select
                                      value={apt.status}
                                      size="small"
                                      sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5 } }}
                                      onChange={(e) => handleStatusChange(apt, e.target.value as AppointmentStatus)}
                                    >
                                      {WORKFLOW_STATUS_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  ) : (
                                    <Chip
                                      label={formatStatusLabel(apt.status)}
                                      size="small"
                                      sx={{ bgcolor: getStatusColor(apt.status), color: 'white' }}
                                    />
                                  ),
                                },
                                { label: 'Time', value: `${formatTimeOnly(aptDate, dateTimeFormat, false)} · ${duration} min` },
                                { label: 'Provider', value: (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorForAppointment(apt), flexShrink: 0 }} />
                                    {apt.provider || '—'}
                                  </Box>
                                ) },
                                {
                                  label: 'Type',
                                  value: (
                                    <Box component="span" sx={{ textTransform: 'capitalize' }}>
                                      {apt.appointmentType?.replace(/_/g, ' ') || '—'}
                                    </Box>
                                  ),
                                },
                                { label: 'Complaint', value: apt.presentingComplaints || '—' },
                                ...(apt.clinicalNotes ? [{ label: 'Notes', value: apt.clinicalNotes }] : []),
                              ]}
                              actions={
                                <>
                                  <AppointmentRowActions
                                    appointment={apt}
                                    onEdit={handleEditAppointment}
                                    onDuplicate={handleDuplicateAppointment}
                                    onCancel={(a) => requestCancelAppointment(a)}
                                    onDelete={(a) => requestDeleteAppointment(a)}
                                  />
                                </>
                              }
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                        <Table size="small" sx={{ minWidth: 720 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 40 }}>Notes</TableCell>
                              <TableCell sx={{ width: 150 }}>Status</TableCell>
                              <TableCell>Patient</TableCell>
                              <TableCell sx={{ width: 130 }}>Time</TableCell>
                              <TableCell>Provider</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Chief Complaint</TableCell>
                              <TableCell sx={{ width: 150 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedAppointments.map(apt => {
                              const aptDate = parseLocalTime(apt.startTime);
                              const isToday = isSameDay(aptDate, new Date());
                              const duration = editorDuration(apt);
                              
                              return (
                                <TableRow key={apt.id} hover>
                                  {/* Notes */}
                                  <TableCell>
                                    {apt.clinicalNotes ? (
                                      <IconButton size="small" title={apt.clinicalNotes}>
                                        <NoteIcon fontSize="small" color="primary" />
                                      </IconButton>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                  </TableCell>
                                  
                                  {/* Status - dropdown for today, static for other days */}
                                  <TableCell>
                                    {isToday && apt.status !== 'cancelled' ? (
                                      <Select
                                        value={apt.status}
                                        size="small"
                                        sx={{ 
                                          minWidth: 130,
                                          fontSize: '0.75rem',
                                          '& .MuiSelect-select': { py: 0.5 }
                                        }}
                                        onChange={(e) => handleStatusChange(apt, e.target.value as AppointmentStatus)}
                                      >
                                        {WORKFLOW_STATUS_OPTIONS.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    ) : (
                                      <Chip 
                                        label={formatStatusLabel(apt.status)} 
                                        size="small"
                                        sx={{ 
                                          bgcolor: getStatusColor(apt.status),
                                          color: 'white',
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  
                                  {/* Patient - Name + Phone/DOB/Gender */}
                                  <TableCell>
                                    <Tooltip title={`Open ${apt.patientName}'s record — you can come straight back`}>
                                      <Typography
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => openPatientForAppointment(apt)}
                                        disabled={openingPatient === apt.id}
                                        sx={{
                                          fontWeight: 600,
                                          display: 'block',
                                          textAlign: 'left',
                                          border: 0,
                                          p: 0,
                                          background: 'none',
                                          font: 'inherit',
                                          color: 'primary.main',
                                          cursor: 'pointer',
                                          '&:hover, &:focus-visible': { textDecoration: 'underline' },
                                          '&:disabled': { opacity: 0.5, cursor: 'default' },
                                        }}
                                      >
                                        {apt.patientName}
                                      </Typography>
                                    </Tooltip>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {[
                                        apt.patientPhone,
                                        apt.patientDOB ? formatDateOnly(apt.patientDOB, dateTimeFormat) : null,
                                        apt.patientGender
                                      ].filter(Boolean).join(' • ')}
                                    </Typography>
                                  </TableCell>
                                  
                                  {/* Time with duration */}
                                  <TableCell>
                                    <Typography variant="body2">
                                      {formatTimeOnly(aptDate, dateTimeFormat, false)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ({duration} min)
                                    </Typography>
                                  </TableCell>
                                  
                                  {/* Provider */}
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorForAppointment(apt), flexShrink: 0 }} />
                                      {apt.provider || '-'}
                                    </Box>
                                  </TableCell>
                                  
                                  {/* Type */}
                                  <TableCell sx={{ textTransform: 'capitalize' }}>
                                    {apt.appointmentType?.replace(/_/g, ' ') || '-'}
                                  </TableCell>
                                  
                                  {/* Chief Complaint */}
                                  <TableCell>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        maxWidth: 200, 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                      title={apt.presentingComplaints || '-'}
                                    >
                                      {apt.presentingComplaints || '-'}
                                    </Typography>
                                  </TableCell>
                                  
                                  {/* Actions */}
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <AppointmentRowActions
                                        appointment={apt}
                                        onEdit={handleEditAppointment}
                                        onDuplicate={handleDuplicateAppointment}
                                        onCancel={(a) => requestCancelAppointment(a)}
                                        onDelete={(a) => requestDeleteAppointment(a)}
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                {(viewMode === 'day' || viewMode === 'week' || viewMode === 'month') && (
                  // Week view can't fit 7 readable columns on a phone (~38px
                  // each, and the day headers wrapped to three lines). Same
                  // approach as the appointments table: give the grid a usable
                  // minimum width and let it scroll inside its own container,
                  // so the page body never scrolls sideways. Day view is a
                  // single column and needs no minimum.
                  <Box
                    sx={{
                      overflowX: 'auto',
                      maxWidth: '100%',
                      '& .fc': { minWidth: viewMode === 'week' ? { xs: 860, md: 'auto' } : 'auto' },
                      ...closedDayStyles,
                      // Keep the day headers on one line at small sizes.
                      '& .fc-col-header-cell-cushion': { whiteSpace: 'nowrap', fontSize: { xs: '0.7rem', md: '0.8rem' } },
                      '& .fc-timegrid-slot-label-cushion': { fontSize: { xs: '0.65rem', md: '0.75rem' } },
                      '& .fc-event-title, & .fc-event-time': { fontSize: { xs: '0.65rem', md: '0.75rem' } },
                      // A 30-minute visit is one half-hour row now, and two
                      // stacked lines no longer fit: the time and the patient
                      // sit on one line, the name trimmed with an ellipsis
                      // rather than cut mid-letter. Longer visits get the same
                      // line, which reads the same at a glance.
                      '& .fc-timegrid-event .fc-event-main-frame': { display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 0.5, overflow: 'hidden' },
                      '& .fc-timegrid-event .fc-event-time': { flex: 'none', mb: 0, whiteSpace: 'nowrap' },
                      '& .fc-timegrid-event .fc-event-title-container': { minWidth: 0 },
                      '& .fc-timegrid-event .fc-event-title': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  >
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView={
                      viewMode === 'month' ? 'dayGridMonth' : viewMode === 'day' ? 'timeGridDay' : 'timeGridWeek'
                    }
                    initialDate={currentDate}
                    headerToolbar={false}
                    events={[...filteredAppointments.map(apt => ({
                      id: apt.id,
                      title: `${apt.patientName}${apt.appointmentType ? ' - ' + apt.appointmentType.replace(/_/g, ' ') : ''}`,
                      // Clinic wall-clock, deliberately with no zone on it.
                      // Passing the ISO instant let the grid render in the
                      // VIEWER's timezone: the same 2:30 PM appointment showed
                      // as 9:30 AM on a machine set to Hawaii. A zone-less
                      // string is treated as floating and placed exactly where
                      // it says, whoever is looking.
                      start: toPracticeWallClock(apt.startTime),
                      end: toPracticeWallClock(apt.endTime),
                      // A cancelled appointment kept the provider's colour and
                      // read as a live booking, so cancelling one from the grid
                      // looked like it had done nothing. It stays visible —
                      // staff want to see that the slot was given up — but
                      // greyed and struck through, and the slot really is free.
                      backgroundColor: apt.status === 'cancelled' ? 'transparent' : colorForAppointment(apt),
                      borderColor: apt.status === 'cancelled' ? '#9ca3af' : colorForAppointment(apt),
                      textColor: apt.status === 'cancelled' ? '#6b7280' : undefined,
                      classNames: apt.status === 'cancelled' ? ['appt-cancelled'] : [],
                      extendedProps: { appointment: apt }
                    })),
                    // Holds, after the appointments: outlined, named, and
                    // clickable through to the task the link belongs to.
                    ...holds.map((h: any) => ({
                      id: `hold-${h.id}`,
                      title: `Held: ${h.patientFirstName}`,
                      start: toPracticeWallClock(h.startTime),
                      end: toPracticeWallClock(h.endTime),
                      backgroundColor: 'transparent',
                      borderColor: colorForAppointment({ provider: h.specialistName, specialistId: h.specialistId } as any),
                      textColor: '#374151',
                      classNames: ['appt-held'],
                      extendedProps: { hold: h },
                    }))]}
                    eventClick={(info) => {
                      const hold = info.event.extendedProps.hold;
                      if (hold) {
                        if (hold.taskId) navigate(`/?taskId=${hold.taskId}`);
                        return;
                      }
                      const apt = info.event.extendedProps.appointment as Appointment;
                      if (apt) handleEditAppointment(apt);
                    }}
                    // FullCalendar has no context-menu hook, so the listener
                    // goes on the element it hands us and comes off again when
                    // it unmounts — events are re-rendered on every navigation,
                    // so leaving these attached would pile up.
                    // On the time grid the row already says when; repeating
                    // it inside the block took half the width and cut the
                    // patient's name. The block shows the patient; the full
                    // line — time, patient, type — is the hover title. The
                    // month grid keeps the time, where there is no ruler.
                    views={{
                      timeGridWeek: { displayEventTime: false },
                      timeGridDay: { displayEventTime: false },
                    }}
                    eventDidMount={(info) => {
                      const clockOf = (d: Date | null) => (d ? formatClockString(`${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`) : '');
                      const whenFrom = clockOf(info.event.start);
                      const whenTo = clockOf(info.event.end);
                      info.el.title = `${whenFrom}${whenTo ? ` – ${whenTo}` : ''} · ${info.event.title}`;
                      const apt = info.event.extendedProps.appointment as Appointment;
                      if (!apt) return;
                      const onContextMenu = (e: MouseEvent) => {
                        e.preventDefault();
                        setEventMenu({ x: e.clientX, y: e.clientY, appointmentId: apt.id });
                      };
                      (info.el as any).__onContextMenu = onContextMenu;
                      info.el.addEventListener('contextmenu', onContextMenu);
                    }}
                    eventWillUnmount={(info) => {
                      const handler = (info.el as any).__onContextMenu;
                      if (handler) info.el.removeEventListener('contextmenu', handler);
                    }}
                    slotMinTime={timeWindow.slotMinTime}
                    slotMaxTime={timeWindow.slotMaxTime}
                    height={isMobile ? 500 : 600}
                    // The whole working day on screen at once, no inner scroll:
                    // half-hour rows (the engine books on the half hour) stretched
                    // to fill the height. Quarter-hour rows left 8–5 a scroll and
                    // a half-empty first screen; staff asked to see the day whole.
                    expandRows={true}
                    allDaySlot={false}
                    nowIndicator={true}
                    slotDuration="00:30:00"
                    slotLabelInterval="01:00"
                    // The practice doesn't book weekends, so Sat/Sun are wasted
                    // width. FullCalendar's own flag — no custom layout needed.
                    // The weekend is shown and greyed rather than hidden: staff
                    // asked to see the shape of the week, and hiding it made the
                    // calendar disagree with the Providers tab.
                    weekends={true}
                    // A month grid's headers stand for weekdays, not dates:
                    // FullCalendar renders them from an arbitrary reference
                    // week, so including the date showed "Mon, Jan 5" above an
                    // August calendar.
                    dayHeaderFormat={viewMode === 'month'
                      ? { weekday: 'short' }
                      : { weekday: 'short', month: 'short', day: 'numeric' }}
                    // On phones render "Sun 23" explicitly: the full format
                    // ("Sun, Aug 23") wrapped to three lines, and the shortened
                    // field set renders as "23 Sun" under en-US ordering.
                    // The compact mobile header pairs the weekday with its date,
                    // which is right for a day or week column. A month grid has
                    // no single date per column — FullCalendar hands it an
                    // arbitrary reference week — so month keeps the plain
                    // weekday at every size.
                    dayHeaderContent={isMobile && viewMode !== 'month'
                      ? (arg: any) => `${arg.date.toLocaleDateString('en-US', { weekday: 'short' })} ${arg.date.getDate()}`
                      : undefined}
                  />
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>

        {/* Appointment Modal */}
        {/* Right-click menu for a calendar event. Deliberately only the two
            things staff do to an appointment they can see: open it, or give
            the slot up. Deleting permanently stays in the Appointments list,
            where it takes a deliberate trip rather than a stray right-click. */}
        <Menu
          open={Boolean(eventMenu && menuAppointment)}
          onClose={() => setEventMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={eventMenu ? { top: eventMenu.y, left: eventMenu.x } : undefined}
        >
          {menuAppointment && (
            <MenuItem
              onClick={() => {
                const apt = menuAppointment;
                setEventMenu(null);
                handleEditAppointment(apt);
              }}
            >
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit appointment</ListItemText>
            </MenuItem>
          )}
          {menuAppointment && canCancelAppointment(menuAppointment) && (
            <MenuItem
              onClick={() => {
                const apt = menuAppointment;
                setEventMenu(null);
                requestCancelAppointment(apt);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Cancel appointment</ListItemText>
            </MenuItem>
          )}
        </Menu>

        <ConfirmDialog
          open={Boolean(pendingConfirm)}
          title={pendingConfirm?.title || ''}
          body={pendingConfirm?.body}
          confirmLabel={pendingConfirm?.confirmLabel || 'Confirm'}
          busy={confirmBusy}
          onConfirm={runPendingConfirm}
          onClose={() => setPendingConfirm(null)}
        />

        <AppointmentModal
          open={isModalOpen}
          appointment={selectedAppointment}
          prefillFrom={duplicateSource}
          onClose={() => { setIsModalOpen(false); setDuplicateSource(null); }}
          onCancelAppointment={(apt) => requestCancelAppointment(apt, () => setIsModalOpen(false))}
          onSave={async (apt) => {
            const wasEdit = Boolean(selectedAppointment);
            if (selectedAppointment) {
              // Update existing appointment
              setAppointments(prev => prev.map(a => a.id === apt.id ? apt : a));
            } else {
              // Add new appointment - use the ID from the API response
              setAppointments(prev => [...prev, apt]);
            }
            setIsModalOpen(false);
            setSelectedAppointment(null);
            await fetchAppointments();
            const patientText = (apt as any).patientText;
            if (wasEdit && patientText) {
              showSnackbar(
                `Appointment moved ${describeAppointment(apt)}. ${patientTextSuffix(patientText)}`,
                patientText.sent ? 'success' : 'error',
              );
            } else {
              showSnackbar(`Appointment ${wasEdit ? 'updated' : 'scheduled'} ${describeAppointment(apt)}`);
            }
          }}
        />

        {/* Quick Schedule Modal */}
        <QuickScheduleModal
          open={isQuickScheduleOpen}
          onClose={() => setIsQuickScheduleOpen(false)}
          onScheduled={async (apt) => {
            setAppointments(prev => [...prev, apt]);
            await fetchAppointments();
            showSnackbar(`Appointment scheduled ${describeAppointment(apt)}`);
          }}
        />

        {/* Confirmation feedback for every appointment mutation. Bottom-right,
            non-blocking — replaces the blocking window.alert() calls, and adds
            the success confirmations that were missing entirely. */}
        <Snackbar
          open={snackbar.open}
          // An undoable message stays up longer: four seconds is enough to
          // read a confirmation, not enough to notice a mistake and act on it.
          autoHideDuration={
            snackbar.severity === 'error' ? 8000 : snackbar.undoTaskId ? 10000 : 4000
          }
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            action={
              snackbar.undoTaskId ? (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => undoWellnessScheduling(snackbar.undoTaskId as string)}
                  sx={{ fontWeight: 700 }}
                >
                  Undo
                </Button>
              ) : undefined
            }
            sx={{ maxWidth: { xs: '92vw', sm: 480 } }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Calendar;
